/**
 * "For members" copy (#3218, the fifth audience page of the #3122 marketing
 * epic). Sister to `leaders.ts`; read that file's header first — the same
 * reasons apply here.
 *
 * WHY THIS IS A DATA MODULE AND NOT INLINE JSX
 * --------------------------------------------
 * `scripts/check-voice.mjs` scans `frontend/src/data/content/*.ts`, and the
 * gallery round-trip edits copy here. Copy written inline in the page is
 * outside both gates. Keeping it here means the voice gate and the editable
 * proof are real for this page from the first commit (#2954).
 *
 * THE ™ IS NOT IN THIS FILE, DELIBERATELY.
 * Core Rule 24 gives the page exactly one mark, on its most prominent use: the
 * hero. The page renders `{hero.brandLead} ORO move<sup>™</sup> {hero.brandRest}`
 * via <OroTrademark />, so the mark stays out of the copy strings. Every other
 * mention ("And then ORO move gets out of the way.", "If your club uses ORO move…",
 * the close) is the bare string. The rendered ™ count is pinned at 1 by
 * `MembersPage.test.tsx`, the same way `LeaderWhyPage.test.tsx` pins /leaders.
 *
 * ARRAYS ARE ARRAYS OF OBJECTS, not bare strings, on purpose (same as
 * `leaders.ts`): the gallery `ts-object` adapter round-trips `lines.0.text`
 * (a keyed property) but cannot write back a bare `'string',` array element
 * (no key to anchor the rewrite). Keep the `{ text }` shape.
 *
 * VOICE (#3218 Gate 2): the owner's two em-dashes were resolved to a colon
 * (hero) and a full stop (the 5:50 message) so `check-voice.mjs` passes. Bare
 * "ORO" matches the house copy in `about.ts` / `leaders.ts` and stays.
 *
 * ANCHOR: "Ten minutes before" (#3122). This page argues to the PARTICIPANT —
 * the person who was handed an invite by their club and is deciding whether to
 * show up — not the organiser (#3125 /leaders) and not the founder story
 * (/about). Boundary + gate record on #3218. Composition (Gate 3, artifact v3):
 * https://claude.ai/code/artifact/0a0dbf74-6a6e-4316-bbb0-44270ffb23bb
 */

export const MEMBERS = {
  /** S1 — hero. Name the reader (invited by your club) and the promise. */
  hero: {
    headline: 'You’re invited.',
    sub1: 'The details shouldn’t get in the way.',
    // Rendered as `{brandLead} ORO move™ {brandRest}` by the page (the ™ lives
    // in <OroTrademark />, so it stays out of these strings). Gate 2 fix: the
    // owner's em-dash after "together" became a colon.
    brandLead: 'Clubs use',
    brandRest:
      'to keep the plan together: where to be, when to be there, and what changed.',
    sub3: 'So when the day comes, your job is pretty simple.',
    showUp: 'Show up.',
    cta: 'See what that looks like',
  },

  /** S2 — the reality. Recognition before solution; no product in this section. */
  reality: {
    eyebrow: 'Before the run',
    title: 'The organizing happens in one place.',
    questions: [
      { text: 'Where are we meeting?' },
      { text: 'Is 6:40 when we meet, or when everyone starts?' },
      { text: 'Does the rain change anything?' },
    ],
    newLead: 'And if you’re new:',
    newPunch: 'Am I going to be the slowest one there?',
    turn: 'None of that is the run.',
    tail: 'It’s just everything you have to think about before you can get there.',
  },

  /** S3 — three things showing up gets you. Each grounded in a shipped
   *  capability; the third pillar ties back to the page's whole argument. */
  pillars: {
    eyebrow: 'What you get',
    title: 'What you need to get outside.',
    items: [
      {
        // `id` keys the pillar to its Lucide mark in the page (PILLAR_ICONS),
        // so adding or reordering a pillar cannot silently mis-pair icons the
        // way a positional index did. Stable across gallery copy edits.
        id: 'plan',
        heading: 'The plan is waiting for you.',
        body: [
          { text: 'Time. Place. What the group is doing.' },
          {
            text: 'RSVP when you know you’re coming. Events can sync to your calendar so you get on with your week.',
          },
        ],
      },
      {
        id: 'updates',
        heading: 'If something changes, you won’t have to go looking for it.',
        body: [
          {
            text: 'Different parking lot? Rain pushed the start back? Someone needs to get a message to the group?',
          },
          { text: 'The event gets updated and notifications arrive the way you like.' },
        ],
      },
      {
        id: 'checkin',
        heading: 'Being there is enough.',
        body: [
          { text: 'Check in when you arrive. (And say hi to someone new!)' },
          { text: 'That’s it.' },
          {
            text: 'No rankings for whether you were first, fastest, or went farthest.',
          },
        ],
        strong: 'Showing up is what counts.',
      },
    ],
  },

  /** S4 — one Saturday, from your side. The seeded Golden Hour data as a
   *  timeline (not the front door's demo cards). `emphasis` bolds one word of
   *  `text`; `tone` styles a whole line (`sub` muted, `msg` the quoted push,
   *  `punch` the payoff). Gate 2 fix: the 5:50 message's em-dash became a stop. */
  saturday: {
    eyebrow: 'One week',
    title: 'A typical week',
    steps: [
      {
        when: 'Thursday',
        lines: [
          { text: 'Your club posts Saturday’s run.' },
          { text: 'You tap “Going”.', emphasis: 'Going' },
        ],
      },
      {
        when: 'Friday',
        lines: [
          { text: 'It’s synced to your calendar.' },
          { text: '6:40 AM. Upper lot.', tone: 'sub' },
        ],
      },
      {
        when: 'Saturday, 5:50 AM',
        lines: [
          { text: 'A message comes through:' },
          { text: 'Lower lot is locked. Park up top. We’re still on.', tone: 'msg' },
          { text: 'No searching through yesterday’s group chat.', tone: 'sub' },
        ],
      },
      {
        when: 'Saturday, 6:40 AM',
        lines: [
          { text: 'You’re there.' },
          { text: 'You check in and head out with everyone else.' },
        ],
      },
      {
        when: 'Sunday',
        lines: [
          { text: 'No wondering.' },
          { text: 'No hunting for details.' },
          { text: 'You just do the important part.' },
          { text: 'You show up.', tone: 'punch' },
        ],
      },
    ],
  },

  /** S5 — what it never asks of you. Every line a true negative today
   *  (ADR-0019: no feed, no uploads, no ranking, no ads). */
  leaveOut: {
    eyebrow: 'What we leave out',
    title: 'And then ORO move gets out of the way.',
    lines: [
      { text: 'There isn’t another feed to get lost in.' },
      { text: 'No workouts to upload.' },
      { text: 'No pace ranking.' },
      { text: 'No ads competing for your attention.' },
    ],
    close: [
      {
        text: 'Your club has a place to make a plan, discuss them and make the details easier.',
      },
      { text: 'Then everybody does their thing.' },
    ],
    together: 'Together.',
  },

  /** S6 — how you get in, and the other door. Boundary with /leaders; no
   *  club-discovery claim (#3401 records discovery as future work). */
  door: {
    eyebrow: 'Getting in',
    title: 'Your club opens the door.',
    lines: [
      { text: 'If your club uses ORO move, your leader sends you an invite.' },
      { text: 'Tap it. Join your club. You’ll find the next event waiting there.' },
      { text: 'No club invite yet? Ask the person who runs your group.' },
    ],
    otherLead: 'Run the club yourself?',
    otherBody: 'We’ve got a page for you, too.',
    otherLink: 'For club leaders',
  },

  /** S7 — close. Restate the calm promise. The page's one gold action opens the
   *  sign-in modal (front-door parity). Below it, a quiet secondary link shares
   *  the /leaders page — there is no invite to route to, so the honest next step
   *  for a reader without a club is to hand it to whoever runs theirs (owner
   *  decision 2026-09-10, replacing the earlier "Join your club"). */
  close: {
    title: 'Know where to be. Then show up.',
    body1: 'Your club makes the plan.',
    body2: 'ORO move keeps you in it.',
    // #3448: the App Store note that sits above the badge under the close CTA.
    // The badge itself is an image (AppStoreBadge), so only this line is copy.
    appStoreNote: 'Also on iPhone.',
    cta: 'Sign in',
    secondary: 'Send this to whoever runs your club',
    shareTitle: 'ORO move for club leaders',
    shareText: 'Take a look at what running the club on ORO move looks like.',
    // Toasts for the clipboard-fallback share path. Here, not inlined in the
    // page, so check-voice scans them and the gallery can edit them (#2954).
    copiedToast: 'Link copied',
    copyFailedToast: 'Failed to copy link',
  },
} as const;
